import React from "react";
import {
  AWAITING_RESPONDANT,
  RESPONDANT_ONBOARDED,
  NEGOTIATION_ONGOING,
  NEGOTIATION_REACHED,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  CLAIMENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  AWAITING_RESPONDANT_QUIT,
} from "../../../helpers/constants";
import CommentBox from "./common/CommentBox";
import CaseAgreement from "./common/CaseAgreement";
import AgreementDocCard from "./common/AgreementDocCard";
import _ from "lodash";

// States where comments are disabled
const DISABLED_COMMENT_STATES = [
  AWAITING_RESPONDANT_QUIT,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  CLAIMENT_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  "finalAward",
];

// States where AgreementSection is shown
const AGREEMENT_STATES = [
  CLAIMENT_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  "reservedForAward",
  "awardPassed",
  "settledAndWithdrawn",
  "updatedAndWithdrawn",
  "finalAward",
];

// States where CommentBox is expanded
const EXPANDED_STATES = [
  AWAITING_RESPONDANT,
  RESPONDANT_ONBOARDED,
  NEGOTIATION_ONGOING,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  NEGOTIATION_REACHED,
  AWAITING_RESPONDANT_QUIT,
  "awaitingRespondentAcceptance",
  "noticeToArbitrate",
  "arbitrationReferenced",
  "arbitrationNominated",
  "arbitrationConfirmed",
  "waitingForArbitratorConfirmation",
  "firstNoticeSent",
  "statementOfClaimFiled",
  "awaitingForSec17Details",
  "sec17PetitionFiled",
  "sec17Ordered",
  "awaitingSec17Execution",
  "sec17ExtensionMemoFiled",
  "sec17ExtensionMemoOrdered",
  "fsOrdered",
  "attachmentOrdered",
  "garnisheeProhibitaryOrder",
  "garnisheeFinalOrder",
  "sec17Closed",
  "statementOfDefenceFiled",
  "rejoinderFromClaimant",
  "surrejoinderFromRespondent",
  "2ndNoticeMOM",
  "evidenceFiledByClaimant",
  "evidenceFiledByRespondent",
  "crossExamination",
  "argumentsByClaimant",
  "argumentsByRespondent",
  "negotiationOngoing",
  "firstHearing",
  "secondHearing",
  "thirdHearing",
  "firstMom",
  "commencementLetter",
  "arbitrationInprogress",
  "referenceLetter",
];

// States where adding comments is hidden
const HIDE_ADD_COMMENT_STATES = [
  "awaitingRespondentAcceptance",
  "noticeToArbitrate",
  "arbitrationReferenced",
  "arbitrationNominated",
  "arbitrationConfirmed",
  "waitingForArbitratorConfirmation",
  "firstNoticeSent",
  "statementOfClaimFiled",
  "awaitingForSec17Details",
  "sec17PetitionFiled",
  "sec17Ordered",
  "awaitingSec17Execution",
  "sec17ExtensionMemoFiled",
  "sec17ExtensionMemoOrdered",
  "fsOrdered",
  "attachmentOrdered",
  "garnisheeProhibitaryOrder",
  "garnisheeFinalOrder",
  "sec17Closed",
  "statementOfDefenceFiled",
  "rejoinderFromClaimant",
  "surrejoinderFromRespondent",
  "2ndNoticeMOM",
  "evidenceFiledByClaimant",
  "evidenceFiledByRespondent",
  "crossExamination",
  "argumentsByClaimant",
  "argumentsByRespondent",
  "negotiationOngoing",
  "firstHearing",
  "secondHearing",
  "thirdHearing",
  "firstMom",
  "arbitrationInprogress",
  "commencementLetter",
  "referenceLetter",
];

const MediationState = ({
  currentState,
  isRespondent,
  ownerId,
  caseDetails,
  showCta,
  comments,
  setComments,
  partyList,
  isLocalTranscriptionServer,
}) => {
  const RenderCommentBox = ({
    status,
    expand,
    errorText,
    comments,
    caseId,
    setComments,
    disabled,
    isLocalTranscriptionServer,
  }) => (
    <CommentBox
      title={`${_.capitalize(caseDetails?.resolutionKind)} Process`}
      expand={expand}
      status={status}
      errorText={errorText}
      ownerId={ownerId}
      caseId={caseId}
      comments={comments}
      disabled={disabled}
      setComments={setComments}
      kind={
        caseDetails?.resolutionKind === "mediation" ? "Mediator" : "Arbitrator"
      }
      hasMediator={
        caseDetails?.requestedMediators?.length &&
        caseDetails?.requestedMediators?.some((el) => el?.id)
      }
      hideAddComment={HIDE_ADD_COMMENT_STATES.includes(currentState?.key)}
      partyList={partyList}
      isLocalTranscriptionServer={isLocalTranscriptionServer}
    />
  );

  const AgreementSection = () => (
    <>
      <div className="mt-7">
        <CaseAgreement
          title={`Final Award Summary for Case ID: ${caseDetails?.id}`}
          message={caseDetails?.negotiationSummary}
          summaryStatus={caseDetails?.summaryStatus === "completed"}
          isPaid={currentState?.cta?.length === 0}
        />
      </div>
      <div className="mt-7">
        <AgreementDocCard
          id={caseDetails?.id}
          status={caseDetails.status}
          isRespondent={isRespondent}
          hasRating={caseDetails?.rating}
          hasAgreement={caseDetails?.hasAgreementDocument}
          type="Download"
        />
      </div>
    </>
  );

  const getCommentBoxProps = () => {
    const baseProps = {
      caseId: caseDetails?.id,
      comments,
      setComments,
      isLocalTranscriptionServer,
      expand: EXPANDED_STATES.includes(currentState?.key),
      disabled: DISABLED_COMMENT_STATES.includes(currentState?.key),
    };

    switch (currentState?.key) {
      case AWAITING_RESPONDANT:
      case RESPONDANT_ONBOARDED:
        return {
          ...baseProps,
          status:
            caseDetails.respondentStatus === "declined" ? "error" : undefined,
          errorText:
            caseDetails.respondentStatus === "declined"
              ? "Respondent Declined the Case"
              : undefined,
        };
      case AWAITING_RESPONDANT_QUIT:
        return {
          ...baseProps,
          status: "error",
          errorText: "Case Closed by Scrutiny Clerk",
        };
      case NEGOTIATION_QUIT_BY_CLAIMENT:
        return {
          ...baseProps,
          status: "error",
          errorText: "Case Quit by Claimant",
        };
      case NEGOTIATION_QUIT_BY_RESPONDENT:
        return {
          ...baseProps,
          status: "error",
          errorText: "Case Quit by Respondent",
        };
      case NEGOTIATION_QUIT_BY_CASEMANAGER:
        return {
          ...baseProps,
          status: "error",
          errorText: "Case Closed by Scrutiny Clerk",
        };
      default:
        return baseProps;
    }
  };

  const renderContent = () => {
    const commentBoxProps = getCommentBoxProps();
    const showAgreement = AGREEMENT_STATES.includes(currentState?.key);

    return (
      <>
        <div className="mt-7">
          <RenderCommentBox {...commentBoxProps} />
        </div>
        {showAgreement && <AgreementSection />}
      </>
    );
  };

  return currentState?.key ? renderContent() : null;
};

export default React.memo(MediationState);
